import { useState, useEffect, useRef } from 'react';

export default function AlbumSelectorDropdown({
  selectedAlbumId,
  onAlbumChange,
  hideFromLibrary,
  onHideChange,
  disabled = false
}) {
  const [open, setOpen] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [createError, setCreateError] = useState('');
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (open && albums.length === 0) {
      setLoading(true);
      fetch('/api/albums', { credentials: 'include' })
        .then(r => r.json())
        .then(data => {
          setAlbums(Array.isArray(data) ? data : data?.albums || []);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
    if (open) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setCreating(false);
        setSearch('');
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const selectedAlbum = albums.find(a => a.id === selectedAlbumId);
  const filtered = albums.filter(a =>
    a.title?.toLowerCase().includes(search.toLowerCase())
  );

  async function handleCreate() {
    if (!newTitle.trim()) return;
    setCreateError('');
    try {
      const res = await fetch('/api/albums', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: newTitle.trim() })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setCreateError(err.message || 'Failed to create album');
        return;
      }
      const album = await res.json();
      setAlbums(prev => [album, ...prev]);
      onAlbumChange(album.id);
      setCreating(false);
      setNewTitle('');
      setOpen(false);
    } catch {
      setCreateError('Failed to create album');
    }
  }

  return (
    <div className="upload-album-selector" ref={dropdownRef}>
      <button
        type="button"
        className={`album-selector-trigger ${open ? 'open' : ''} ${selectedAlbumId ? 'has-album' : ''}`}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="9" y1="3" x2="9" y2="21" />
        </svg>
        <span>{selectedAlbum ? selectedAlbum.title : 'No album (All Photos)'}</span>
        <svg className="chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="album-selector-dropdown">
          <div className="album-selector-search">
            <input
              ref={searchRef}
              type="text"
              placeholder="Search albums..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <div className="album-selector-options">
            <button
              type="button"
              className={`album-selector-option ${!selectedAlbumId ? 'selected' : ''}`}
              onClick={() => {
                onAlbumChange(null);
                onHideChange(false);
                setOpen(false);
                setSearch('');
              }}
            >
              <span className="option-label">None (All Photos)</span>
            </button>

            {loading && <div className="album-selector-loading">Loading albums...</div>}

            {filtered.map(album => (
              <button
                key={album.id}
                type="button"
                className={`album-selector-option ${selectedAlbumId === album.id ? 'selected' : ''}`}
                onClick={() => {
                  onAlbumChange(album.id);
                  setOpen(false);
                  setSearch('');
                }}
              >
                <span className="option-label">{album.title}</span>
                {album.image_count != null && (
                  <span className="option-count">{album.image_count}</span>
                )}
              </button>
            ))}

            {!loading && filtered.length === 0 && search && (
              <div className="album-selector-empty">No albums match "{search}"</div>
            )}
          </div>

          <div className="album-selector-create">
            {creating ? (
              <div className="album-create-form">
                <input
                  type="text"
                  placeholder="Album title"
                  value={newTitle}
                  onChange={e => setNewTitle(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleCreate();
                    if (e.key === 'Escape') { setCreating(false); setNewTitle(''); }
                  }}
                  autoFocus
                />
                <button type="button" onClick={handleCreate} disabled={!newTitle.trim()}>
                  Create
                </button>
                {createError && <div className="album-create-error">{createError}</div>}
              </div>
            ) : (
              <button type="button" className="album-create-trigger" onClick={() => setCreating(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Create new album
              </button>
            )}
          </div>
        </div>
      )}

      {selectedAlbumId && (
        <label className="hide-from-library-checkbox">
          <input
            type="checkbox"
            checked={hideFromLibrary}
            onChange={e => onHideChange(e.target.checked)}
            disabled={disabled}
          />
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
          <span>Hide from All Photos</span>
        </label>
      )}
    </div>
  );
}
